import { FC, useEffect, useState } from "react";
import Modal from "@albertsons/uds/molecule/Modal";
import {
  UserRole,
  AccessRequestPayload,
  UserState,
} from "../../../../types/auth.types";
import Input from "@albertsons/uds/molecule/Input";
import Select, { Option } from "@albertsons/uds/molecule/Select";
import { Button } from "@albertsons/uds/molecule/Button";

interface ProvideAccessModalProps {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  provideAccess: (data: AccessRequestPayload) => void;
  onClose: () => void;
}

const ProvideAccessModal: FC<ProvideAccessModalProps> = (props) => {
  const { isOpen, isLoading, error, provideAccess, onClose } = props;

  const [emailInput, setEmailInput] = useState("");
  const [roleInput, setRoleInput] = useState(UserRole.USER);
  const [errors, setErrors] = useState({
    email: "",
    role: "",
  });

  const validateEmail = (email: string) => {
    const re = /^[a-z0-9]+@safeway\.com$/i;
    return re.test(email);
  };

  const submit = () => {
    setErrors({
      email: "",
      role: "",
    });
    if (!emailInput) {
      setErrors((prev) => ({ ...prev, email: "Email is required!" }));
      return;
    }
    if (!validateEmail(emailInput)) {
      setErrors((prev) => ({ ...prev, email: "Email is invalid!" }));
      return;
    }
    if (!roleInput) {
      setErrors((prev) => ({ ...prev, role: "Role is required!" }));
      return;
    }

    const payload: AccessRequestPayload = {
      email: emailInput,
      state: UserState.APPROVED,
      role: roleInput,
      userNote: "",
      comments: "",
    };

    provideAccess(payload);
  };

  useEffect(() => {
    // Reset the form when modal opens
    if (isOpen) {
      setEmailInput("");
      setRoleInput(UserRole.USER); // Default to USER role
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      minHeight={20}
      minWidth={20}
      width={500}
      maxWidth="calc(100vw - 40px)"
      className="bg-white overflow-hidden !rounded-lg"
    >
      {({ close }) => (
        <div data-testid="provide-access-modal">
          <div className="flex h-12 p-2 bg-neutral-100">
            <div className="flex text-center items-center justify-center font-bold w-full px-3">
              <p>Please provide user details below</p>
            </div>
          </div>
          <div className="flex flex-col gap-3 p-5">
            <div className="mb-4">
              <label htmlFor="email">Email</label>
              <Input
                type="text"
                name="email"
                autoFocus
                placeholder="Enter user email..."
                optionalHelperText="Email should be alpha numeric and end with @safeway.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                error={errors.email || error ? true : false}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>
            <div className="mb-4">
              <label htmlFor="role-select">Role</label>
              <Select
                className="w-full mt-[2px] border border-[#e5e7eb] text-sm mb-2"
                size="md"
                name="role-select"
                value={roleInput}
                onChange={(item: any) => {
                  setRoleInput(item);
                }}
                error={errors.role ? true : false}
                options={{
                  strategy: "fixed",
                }}
                disabled={isLoading}
              >
                <Option key={UserRole.USER} item={UserRole.USER} />
                <Option key={UserRole.DEV} item={UserRole.DEV} />
                <Option key={UserRole.QA} item={UserRole.QA} />
                <Option key={UserRole.ADMIN} item={UserRole.ADMIN} />
              </Select>
              {errors.role && (
                <p className="text-red-500 text-sm mt-1">{errors.role}</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                fixed
                variant="secondary"
                className="!w-20"
                onClick={close}
                disabled={isLoading}
                data-testid="access-control-cancel-button"
              >
                Cancel
              </Button>
              <Button
                fixed
                className="!w-20"
                onClick={submit}
                disabled={isLoading}
                data-testid="access-control-submit-button"
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ProvideAccessModal;
